import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Table,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Popconfirm,
  message,
  Tooltip,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const { TextArea } = Input;
const { Title, Text } = Typography;

const StrategyManagement = ({ planId, planData, positionId }) => {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const handleRowClick = (record) => {
    // Navigate to the plan that this strategy belongs to
    navigate(`/top-plans/${planId}`);
  };

  useEffect(() => {
    if (planId) {
      fetchStrategies();
    }
  }, [planId]);

  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      const url = `${process.env.REACT_APP_API_URL}v1/strategies/plan/${planId}`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: token,
        },
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStrategies(data.data || []);
      } else {
        message.error(data.message || 'Failed to fetch strategies');
      }
    } catch (error) {
      message.error('Failed to fetch strategies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStrategy = () => {
    setEditingStrategy(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditStrategy = (strategy) => {
    setEditingStrategy(strategy);
    form.setFieldsValue({
      name: strategy.name,
      description: strategy.description,
      objectives: strategy.objectives,
      successMetrics: strategy.successMetrics,
      implementationSteps: strategy.implementationSteps,
      startDate: strategy.startDate ? moment(strategy.startDate) : null,
      endDate: strategy.endDate ? moment(strategy.endDate) : null,
    });
    setModalVisible(true);
  };

  const handleViewStrategy = (strategy) => {
    setEditingStrategy(strategy);
    setViewModalVisible(true);
  };

  const handleDeleteStrategy = async (strategyId) => {
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/strategies`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ id: strategyId }),
        }
      );
      const data = await response.json();
      if (data.success) {
        message.success('Strategy deleted successfully');
        fetchStrategies();
      } else {
        message.error(data.message || 'Failed to delete strategy');
      }
    } catch (error) {
      message.error('Failed to delete strategy');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      const url = editingStrategy
        ? `${process.env.REACT_APP_API_URL}v1/strategies`
        : `${process.env.REACT_APP_API_URL}v1/strategies`;
      
      const method = editingStrategy ? 'PUT' : 'POST';
      const body = editingStrategy
        ? { id: editingStrategy.id, ...values }
        : { ...values, planId, unitId: planData?.unitId };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.success) {
        message.success(
          editingStrategy ? 'Strategy updated successfully' : 'Strategy created successfully'
        );
        setModalVisible(false);
        fetchStrategies();
      } else {
        message.error(data.message || 'Failed to save strategy');
      }
    } catch (error) {
      message.error('Failed to save strategy');
    }
  };

  const getApprovalStatusTag = (status) => {
    const statusConfig = {
      Draft: { color: 'default', icon: <ClockCircleOutlined /> },
      Pending: { color: 'processing', icon: <ClockCircleOutlined /> },
      Approved: { color: 'success', icon: <CheckCircleOutlined /> },
      Rejected: { color: 'error', icon: <CloseCircleOutlined /> },
    };
    
    const config = statusConfig[status] || statusConfig.Draft;
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Strategy',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>,
    },

    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Strategy">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewStrategy(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit Strategy">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEditStrategy(record);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this strategy?"
            onConfirm={() => handleDeleteStrategy(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Strategy">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="strategy-management">
      <Card
        title={
          <div className="flex justify-between items-center">
            <Title level={4} style={{ margin: 0 }}>
              Strategies
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateStrategy}
            >
              Add Strategy
            </Button>
          </div>
        }
        className="mb-4"
      >
        <Table
          columns={columns}
          dataSource={strategies}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="small"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* Create/Edit Strategy Modal */}
      <Modal
        title={editingStrategy ? 'Edit Strategy' : 'Create Strategy'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            approvalStatus: 'Draft',
          }}
        >
          <Form.Item
            name="name"
            label="Strategy"
            rules={[{ required: true, message: 'Please enter strategy name' }]}
          >
            <Input placeholder="Enter strategy name" />
          </Form.Item>



          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Effective Start Date"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select effective start date"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="Effective End Date"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select effective end date"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="objectives"
            label="Objectives"
          >
            <TextArea
              rows={3}
              placeholder="Enter strategy objectives"
            />
          </Form.Item>

          <Form.Item
            name="successMetrics"
            label="Success Metrics"
          >
            <TextArea
              rows={3}
              placeholder="Enter success metrics"
            />
          </Form.Item>

          <Form.Item
            name="implementationSteps"
            label="Implementation Steps"
          >
            <TextArea
              rows={4}
              placeholder="Enter implementation steps"
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" htmlType="submit">
                {editingStrategy ? 'Update Strategy' : 'Create Strategy'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Strategy Modal */}
      <Modal
        title="Strategy Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {editingStrategy && (
          <div>
            <Row gutter={16} className="mb-4">
              <Col span={12}>
                <Text strong>Name:</Text>
                <div>{editingStrategy.name}</div>
              </Col>
              <Col span={12}>
                <Text strong>Status:</Text>
                <div>{getApprovalStatusTag(editingStrategy.approvalStatus)}</div>
              </Col>
            </Row>

            <Row gutter={16} className="mb-4">
              <Col span={12}>
                <Text strong>Created By:</Text>
                <div>
                  {editingStrategy.creator?.users?.firstName} {editingStrategy.creator?.users?.lastName}
                  {editingStrategy.creator?.designations?.name && ` (${editingStrategy.creator.designations.name})`}
                </div>
              </Col>
              <Col span={12}>
                <Text strong>Created On:</Text>
                <div>{moment(editingStrategy.createdOn).format('DD.MM.YYYY')}</div>
              </Col>
            </Row>

            <Row gutter={16} className="mb-4">
              <Col span={12}>
                <Text strong>Effective Start Date:</Text>
                <div>{editingStrategy.startDate ? moment(editingStrategy.startDate).format('DD.MM.YYYY') : '-'}</div>
              </Col>
              <Col span={12}>
                <Text strong>Effective End Date:</Text>
                <div>{editingStrategy.endDate ? moment(editingStrategy.endDate).format('DD.MM.YYYY') : '-'}</div>
              </Col>
            </Row>



            {editingStrategy.objectives && (
              <div className="mb-4">
                <Text strong>Objectives:</Text>
                <div>{editingStrategy.objectives}</div>
              </div>
            )}

            {editingStrategy.successMetrics && (
              <div className="mb-4">
                <Text strong>Success Metrics:</Text>
                <div>{editingStrategy.successMetrics}</div>
              </div>
            )}

            {editingStrategy.implementationSteps && (
              <div className="mb-4">
                <Text strong>Implementation Steps:</Text>
                <div>{editingStrategy.implementationSteps}</div>
              </div>
            )}

            <div className="text-right">
              <Button onClick={() => setViewModalVisible(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StrategyManagement;

